import { useReveal } from "@/hooks/useReveal";
import { cn } from "@/lib/utils";

interface StackCard {
  title: string;
  description: string;
  image?: string;
  eyebrow?: string;
}

interface StackingCardsProps {
  cards: StackCard[];
  className?: string;
}

/**
 * Stacking cards that reveal on scroll with a layered editorial feel.
 * Each card overlaps the previous slightly, creating depth.
 */
const StackingCards = ({ cards, className }: StackingCardsProps) => {
  return (
    <div className={cn("relative", className)}>
      {cards.map((card, i) => (
        <StackingCardItem key={i} card={card} index={i} total={cards.length} />
      ))}
    </div>
  );
};

const StackingCardItem = ({
  card,
  index,
  total,
}: {
  card: StackCard;
  index: number;
  total: number;
}) => {
  const { ref, revealed } = useReveal(0.2);
  const isLast = index === total - 1;

  return (
    <div
      ref={ref}
      className={cn(
        "sticky top-24 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
        !isLast && "mb-6",
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
      style={{
        transitionDelay: `${index * 80}ms`,
        zIndex: index + 1,
      }}
    >
      <div
        className="bg-card border border-border overflow-hidden shadow-[0_4px_24px_-6px_rgba(0,0,0,0.08)]"
        style={{
          transform: `scale(${1 - (total - 1 - index) * 0.015})`,
        }}
      >
        <div className={cn(
          "grid grid-cols-1",
          card.image && "md:grid-cols-2"
        )}>
          {card.image && (
            <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
              <img
                src={card.image}
                alt={card.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-8 md:p-12 flex flex-col justify-center">
            {card.eyebrow && (
              <span className="font-body text-[10px] uppercase tracking-[0.25em] text-accent mb-3 block">
                {card.eyebrow}
              </span>
            )}
            <h3 className="font-headline text-xl md:text-2xl lg:text-3xl text-foreground mb-4">
              {card.title}
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
              {card.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StackingCards;
